/**
 * Helper: Bed Visualizer
 * -----------------------------------------------------------------------------
 * Formats values and objects.
 *
 */

import { getLineItemProperties } from '~/helpers/product'

/**
 * Sanitizes image alt and checks for existence of text.
 * @param {Object} image - Image object.
 * @param {String|RegExp} text - Text to check for.
 * @returns {Boolean}
 */
export function altContains(image, text) {
  const sanitizedAlt = image?.alt?.toLowerCase()

  if (!sanitizedAlt) {
    return false
  }

  if (text instanceof RegExp) {
    return text.test(sanitizedAlt)
  }

  return sanitizedAlt.includes(text)
}

/**
 * Apply filters to variants.
 * @param {Object} filters - key:value pairs where "key" is the option name
 * and the value is an array of valid values.
 * @param {Array} variants - Product variants.
 * @returns {Array}
 */
export function filterVariants(variants, filters) {

  /**
   * If error state return empty array to avoid rendering product-chip
   * loading states.
   */
  if (!variants) {
    return []
  }

  const filterNames = Object.keys(filters)
  const availableVariants = variants.filter(variant => variant.available)

  /**
   * If no filters exist return all available products.
   */
  if (!filterNames.length) {
    return availableVariants
  }

  const filteredVariants = availableVariants.filter((variant) => {
    let matchesFilters = true

    /**
     * Check all filters in config to see if variant matches.
     */
    filterNames.forEach(name => {
      const value = getOptionValueByName(variant, name)

      /**
       * If filter name doesn't exist a variant option don't proceed.
       *
       * This is to avoid hiding products when the filter can't be met
       * (e.g. a pillow can't be size:queen, so we don't want to hide it
       * if size doesn't exist as an option)
       *
       * TODO: When building out filter logic, this will have to be changed
       * so it is scoped to each unique customize step. With the exception
       * of size which should apply to headboard, bedding and sheet set.
       */
      if (!value) {
        return
      }

      if (filters[name].indexOf(value.toLowerCase()) === -1) {
        matchesFilters = false
        return
      }
    })

    return matchesFilters
  })

  return filteredVariants
}

/**
 * Format variant into a compatible object for the Visualizer store.
 *
 * @param {Boolean} omitVisualizer - Omit visualizer images.
 * @param {Object} product - Canvas formatted product.
 * @param {Array} stackOrder - Stack order of variant.
 * @param {Object} variant - Product variant object.
 * @returns {Object}
 */
 export function formatVariant(product, variant, stackOrder, omitVisualizer = false) {
  return {
    collections: product?.collections || [],
    compareAtPrice: variant.compareAtPrice?.amount || variant.compare_at_price,
    id: variant.id,
    productId: product.id,
    images: {
      thumbnail: getThumbnailImage(product, variant),
      visualizer: omitVisualizer === true ? [] : getVisualizerImages(product, variant),
    },
    price: variant.price?.amount || variant.price,
    properties: {
      ...getLineItemProperties(product, variant),
      _source: 'bed-visualizer',
    },
    stackOrders: stackOrder,
    productTitle: product.title,
    title: variant.title,
  }
}

/**
 * Get option value by name.
 * @param {Object} variant - Variant object.
 * @param {String} name - Option name.
 * @returns {String}
 */
export function getOptionValueByName(variant, name) {
  const namedOption = variant.selectedOptions.find(
    option => option.name.toLowerCase() === name.toLowerCase(),
  )

  return namedOption ? namedOption.value : ''
}

/**
 * Get thumbnail image.
 * @param {Object} product - Product object.
 * @param {Object} variant - Product variant object.
 * @returns {Object|Boolean}
 */
export function getThumbnailImage(product, variant) {
  if (!product?.images || !variant?.image) {
    return false
  }

  return product.images.find(image => image.id === variant.image)
}

/**
 * Get most relevant visualizer images. Supports up to 2 images using
 * 'visualizer' and 'visualizer 2' prefixes.
 *
 * Example: A gallery with 'visualizer green' and 'visualizer 2 green' will
 * return an array with both images.
 *
 * @param {Object} product - Product object.
 * @param {Object} variant - Product variant object.
 * @returns {Array}
 */
export function getVisualizerImages(product, variant) {
  if (!product.images.length) {
    return []
  }

  const matchingImages = []

  const color = getOptionValueByName(variant, 'color').toLowerCase()
  const size = getOptionValueByName(variant, 'size').toLowerCase()

  /**
   * Check if image matches color value.
   * @param {Object} image - Image object.
   * @returns {Boolean}
   */
  const matchesColor = (image) => altContains(image, ` ${color}`)

  /**
   * Check if image matches size value.
   * @param {Object} image - Image object.
   * @returns {Boolean}
   */
  const matchesSize = (image) => altContains(image, ` ${size}`)

  /**
   * Get the closest match from a set of visualizer images.
   * Match priority as follows:
   *   1. Matches color AND size
   *   2. Matches color
   *   3. Matches size
   *   4. First 'visualizer' image
   *   5. Return false if no matches
   * @param {Array} images - Images to search.
   * @returns {Boolean}
   */
  const getMatch = images => {
    if (!images.length) {
      return false
    }

    const fullMatch = images.find(image => matchesColor(image) && matchesSize(image))
    const colorMatch = images.find(matchesColor)
    const sizeMatch = images.find(matchesSize)

    return fullMatch || colorMatch || sizeMatch || images[0]
  }

  const primaryImages = product.images.filter(image => altContains(image, /visualizer(?! 2)/))
  const secondaryImages = product.images.filter(image => altContains(image, 'visualizer 2'))
  const primaryImage = getMatch(primaryImages)
  const secondaryImage = getMatch(secondaryImages)

  if (primaryImage) {
    matchingImages.push(primaryImage)
  }

  if (secondaryImage) {
    matchingImages.push(secondaryImage)
  }

  return matchingImages
}
